import { splitAmount, fmtUsd, fmtPct } from '../../lib/format';
import { FigIcon } from '../FigIcon';
import cardBg from '../../assets/figma/balance-card-bg.png';
import visaCard from '../../assets/figma/visa-card.png';

interface Props {
  balanceUsd: number;
  changePct: number;
  changeUsd: number;
  /** True while the first on-chain read is in flight and there is no local snapshot. */
  loading?: boolean;
  /** Last 4 of the selected card; omit the chip when the user has no cards. */
  cardLast4?: string | null;
  onSelectCard?: () => void;
}

/** Balance card — the gradient + dotted world map are the exact Figma render
 *  (node 2:4564, exported at 3× and clipped to the card), so no CSS approximation
 *  of the map's scale/rotation is needed; text is overlaid on top. */
export function BalanceCard({
  balanceUsd,
  changePct,
  changeUsd,
  loading,
  cardLast4,
  onSelectCard,
}: Props) {
  const { int, frac } = splitAmount(balanceUsd);
  return (
    <div
      className="relative aspect-[370/216] overflow-hidden rounded-[36px] bg-cover bg-center px-5 pb-5 pt-6 text-white shadow-balance"
      style={{ backgroundImage: `url(${cardBg})` }}
    >
      <div className="relative flex h-full flex-col justify-center">
        <p className="text-center text-[15px] font-medium text-white/80">Total balance</p>
        {loading ? (
          <div className="mt-2 flex flex-col items-center gap-3">
            <span className="h-10 w-44 animate-pulse rounded-xl bg-card/25" />
            <span className="h-8 w-28 animate-pulse rounded-full bg-card/20" />
          </div>
        ) : (
          <>
            <div className="mt-1 flex items-end justify-center">
              <span className="text-[40px] font-bold leading-none tracking-tight">${int}</span>
              <span className="pb-1 text-[24px] font-semibold text-white/75">.{frac}</span>
            </div>
            {/* 24h balance-change pill — frosted chip (Figma node 2:4573). The move is
                the sum of each holding's value change over 24h (USDT + TRX, each by its
                own 24h %). No CSS backdrop blur (renders unreliably over the rotated
                dots) — the pill uses OPAQUE composites: the Figma gain colour when up
                (sampled rgb(104,197,229)→rgb(78,184,227), green text), and a red chip
                with light text when down. */}
            <div className="mt-3 flex justify-center">
              <span
                className="flex items-center gap-[10px] rounded-full px-[10px] py-[8px] text-[15px] font-medium"
                style={{
                  background:
                    changeUsd >= 0
                      ? 'linear-gradient(90deg, rgb(104,197,229) 0%, rgb(78,184,227) 100%)'
                      : 'linear-gradient(90deg, rgba(255,108,108,0.42) 0%, rgba(238,80,80,0.48) 100%)',
                  color: changeUsd >= 0 ? '#7bedba' : '#ffffff',
                }}
              >
                {fmtPct(changePct)}
                <span
                  className="inline-block size-[3px] rounded-full"
                  style={{ background: changeUsd >= 0 ? '#7bedba' : '#ffffff' }}
                />
                {fmtUsd(changeUsd)}
              </span>
            </div>
          </>
        )}
        {/* card selector — mini VISA card + ····last4 + up/down chevron (Figma 2:4577) */}
        {cardLast4 ? (
          <button
            onClick={onSelectCard}
            className="mx-auto mt-4 flex items-center gap-[5px] rounded-full px-1 py-1 active:opacity-80"
          >
            <img src={visaCard} alt="VISA" className="h-[24px] w-[31px] rounded-[5px] object-cover" />
            <span className="text-[16px] font-medium tracking-wide text-white">····{cardLast4}</span>
            <FigIcon name="visaChevron" size={18} className="text-white" />
          </button>
        ) : null}
      </div>
    </div>
  );
}
